import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatCurrency } from '../../utils/formatters';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Clock,
  MapPin,
  DollarSign,
  FileText,
  Zap
} from 'lucide-react';

function formatTime12(time24) {
  if (!time24) return '';
  return new Date(`2000-01-01T${time24}`).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  });
}

function TemplateModal({ template, hierarchy, roles, onSave, onClose }) {
  const [name, setName] = useState(template?.name || '');
  const [locationId, setLocationId] = useState(template?.locationId || '');
  const [startTime, setStartTime] = useState(template?.startTime || '20:00');
  const [endTime, setEndTime] = useState(template?.endTime || '00:00');
  const [clientBilled, setClientBilled] = useState(template?.clientBilled || '');
  const [defaultPayout, setDefaultPayout] = useState(template?.defaultPayout || '');
  const [defaultRole, setDefaultRole] = useState(template?.defaultRole || '');
  const [notes, setNotes] = useState(template?.notes || '');
  const [saving, setSaving] = useState(false);

  // Build flat list of locations for dropdown
  const locations = [];
  hierarchy.forEach(client => {
    client.venues?.forEach(venue => {
      venue.locations?.forEach(loc => {
        locations.push({
          id: loc.id,
          label: `${client.name} → ${venue.name} → ${loc.name}`,
          defaultClientBilled: loc.defaultClientBilled,
          defaultPayout: loc.defaultPayout
        });
      });
    });
  });

  // Auto-generate name when location and times change
  useEffect(() => {
    if (!template?.id && locationId && startTime && endTime) {
      const loc = locations.find(l => l.id === locationId);
      if (loc) {
        const locName = loc.label.split(' → ').pop();
        const start = formatTime12(startTime);
        const end = formatTime12(endTime);
        setName(`${locName} ${start}-${end}`);
      }
    }
  }, [locationId, startTime, endTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        name,
        locationId,
        startTime,
        endTime,
        clientBilled: clientBilled ? parseFloat(clientBilled) : null,
        defaultPayout: defaultPayout ? parseFloat(defaultPayout) : null,
        defaultRole: defaultRole || null,
        notes
      };
      
      if (template?.id) {
        await api.put(`/templates/${template.id}`, data);
      } else {
        await api.post('/templates', data);
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedLocation = locations.find(l => l.id === locationId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-hub-600" />
            {template?.id ? 'Edit Template' : 'New Template'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Template Name *</label>
            <input 
              value={name} 
              onChange={e => setName(e.target.value)} 
              className="input" 
              required 
              placeholder="e.g. Eye Candy 9:30-1:30" 
            />
            <p className="text-xs text-gray-500 mt-1">Auto-generated from location + times</p>
          </div>

          <div>
            <label className="label">Location *</label>
            <select 
              value={locationId} 
              onChange={e => setLocationId(e.target.value)} 
              className="input" 
              required
            >
              <option value="">Select location...</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Start Time *</label>
              <input 
                type="time" 
                value={startTime} 
                onChange={e => setStartTime(e.target.value)} 
                className="input" 
                required 
              />
            </div>
            <div>
              <label className="label">End Time *</label>
              <input 
                type="time" 
                value={endTime} 
                onChange={e => setEndTime(e.target.value)} 
                className="input" 
                required 
              />
            </div>
          </div>

          <div>
            <label className="label">Default Role</label>
            <select 
              value={defaultRole} 
              onChange={e => setDefaultRole(e.target.value)} 
              className="input"
            >
              <option value="">Select role...</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Auto-fills contractor role when using this template</p>
          </div>

          <div className="pt-2 border-t">
            <p className="text-sm font-medium text-gray-700 mb-1">Billing Override (optional)</p>
            <p className="text-xs text-gray-500 mb-3">
              Leave blank to use location defaults
              {selectedLocation && (selectedLocation.defaultClientBilled || selectedLocation.defaultPayout) && (
                <span className="text-hub-600">
                  {' '}(${selectedLocation.defaultClientBilled || 0} / ${selectedLocation.defaultPayout || 0})
                </span>
              )}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Client Billed</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" 
                    value={clientBilled} 
                    onChange={e => setClientBilled(e.target.value)} 
                    className="input pl-7" 
                    placeholder={selectedLocation?.defaultClientBilled || '0'}
                    min="0" 
                    step="1" 
                  />
                </div>
              </div>
              <div>
                <label className="label">Contractor Payout</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" 
                    value={defaultPayout} 
                    onChange={e => setDefaultPayout(e.target.value)} 
                    className="input pl-7" 
                    placeholder={selectedLocation?.defaultPayout || '0'}
                    min="0" 
                    step="1" 
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea 
              value={notes} 
              onChange={e => setNotes(e.target.value)} 
              className="input" 
              rows={2} 
              placeholder="Optional notes for gigs created from this template" 
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null or { template } for edit

  const loadData = async () => {
    try {
      const [t, h, r] = await Promise.all([
        api.get('/templates'),
        api.get('/locations/hierarchy'),
        api.get('/roles')
      ]);
      setTemplates(t);
      setHierarchy(h);
      setRoles(r);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/templates/${id}`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shift Templates</h1>
          <p className="text-sm text-gray-500 mt-1">Quick-add gigs from the scheduler</p>
        </div>
        <button onClick={() => setModal({})} className="btn-primary">
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
            <p className="text-gray-500 mb-4">
              Create templates for recurring gigs to speed up scheduling
            </p>
            <button onClick={() => setModal({})} className="btn-primary">
              <Plus className="w-4 h-4" />
              Create First Template
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <div 
              key={template.id} 
              className="card hover:shadow-md transition-shadow"
              style={template.location?.venue?.client?.color ? { 
                borderLeft: `4px solid ${template.location.venue.client.color}` 
              } : undefined}
            >
              <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-hub-600" />
                      {template.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => setModal({ template })}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button 
                      onClick={() => handleDelete(template.id, template.name)}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>
                      {template.location?.venue?.client?.name} → {template.location?.venue?.name} → {template.location?.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{formatTime12(template.startTime)} - {formatTime12(template.endTime)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span>
                      {template.clientBilled 
                        ? formatCurrency(template.clientBilled)
                        : template.location?.defaultClientBilled 
                          ? `${formatCurrency(template.location.defaultClientBilled)} (default)`
                          : 'No default'
                      }
                      {' / '}
                      {template.defaultPayout 
                        ? formatCurrency(template.defaultPayout)
                        : template.location?.defaultPayout 
                          ? `${formatCurrency(template.location.defaultPayout)} (default)`
                          : 'No default'
                      }
                    </span>
                  </div>

                  {template.notes && (
                    <div className="flex items-start gap-2 text-gray-500">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                      <span className="truncate">{template.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <TemplateModal
          template={modal.template}
          hierarchy={hierarchy}
          roles={roles}
          onSave={() => { setModal(null); loadData(); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
